import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Heart,
  HelpCircle,
  IdCard,
  LogOut,
  Luggage,
  Mail,
  MapPin,
  Medal,
  MessageCircle,
  Settings as SettingsIcon,
  ShieldCheck,
  Sparkles,
  Star,
  User,
  X,
  type LucideIcon,
} from "lucide-react";
import { unwrap } from "@majourneys/api-client";
import { isSupportOnline, SUPPORT_OPENS_HOUR } from "@majourneys/booking";
import { formatSupportHour, supportHoursVars } from "@majourneys/shared";
import { WHATSAPP_NUMBER_E164, formatTimeRange, paysByLink, showsActivityPrice } from "@majourneys/shared";
import { BEVEL_INSET_NAVY, BEVEL_NAVY_FACE, CONTROL_EDGE, CONTROL_EDGE_ON, CONTROL_LIT, solid } from "@majourneys/shared/ui";
import { SHEET_BOTTOM, SHEET_INSET, SelectMenu, useMenuGroup, type MenuGroup } from "@majourneys/ui";
import { useCurrency, type CurrencyCode } from "@majourneys/currency";
import { api } from "../../../lib/api";
import { signOut, useSession } from "../../../lib/auth";
import { formatNumber } from "../../../lib/format";
import { useDisplayPrice } from "../../../lib/currency";
import { switchLocale } from "../../../shared/i18n/language-switcher";
import { panelStyle } from "../../../shared/ui/card-frame";
import { useSavePreferences } from "../hooks/use-preference-sync";
import { accountKeys } from "../constants/query-keys";
import { profileApi } from "../api/profile.api";
import type { Reservation } from "../api/reservations.api";
import { useMyProfile } from "../hooks/use-my-profile";
import { useMyReservations } from "../hooks/use-my-reservations";
import { bookingPageHref } from "../lib/booking-page-href";
import { useMySaved } from "../hooks/use-my-saved";
import { useMyReviews } from "../hooks/use-my-reviews";
import { useSaved } from "../../saved/state/saved-context";
import { SecurityPanel } from "./account-security";

/* ------------------------------------------------------------------ */
/* Pewter tokens. Reading surfaces wear the band (panelStyle); things  */
/* you press are white with one hairline (CONTROL); dark surfaces wear */
/* nothing. Inline styles on purpose: Tailwind v4 drops arbitrary      */
/* shadow values containing commas.                                    */
/* ------------------------------------------------------------------ */

const NAVY = "#110E39";
const GOLD = "#E5B265";
const GOLD_DARK = "#8A6425";
const MUTED = "#5a5d77";
const HAIRLINE = "rgba(17,14,57,0.09)";
const SOFT_TINT = "rgba(17,14,57,0.055)";
const GOLD_TINT = "rgba(229,178,101,0.25)";
const NAVY_GRADIENT = "linear-gradient(135deg, #2E2878 0%, #1A1454 48%, #110E39 100%)";
const ONLINE = "#1FA855";
const WHATSAPP_GREEN = "#25D366";
const SHEET_FILL = "linear-gradient(180deg, #FBFBFD 0%, #F3F4F9 100%)";
const FLOAT_SHADOW = "0 18px 44px -18px rgba(17,14,57,0.45)";
/** The phone sheet's band: its own fill, the same float as the popup. */
const SHEET_BAND = panelStyle(28, SHEET_FILL);
const SHEET_FRAME = { ...SHEET_BAND, boxShadow: `${SHEET_BAND.boxShadow}, ${FLOAT_SHADOW}` };

const CONTROL = { background: CONTROL_LIT, boxShadow: `0 0 0 1px ${CONTROL_EDGE}` } as const;
const PANEL = panelStyle(18);

const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER_E164.replace(/^\+/, "")}`;

/** Phones get the sheet, everything from md up the popup with a sidebar. */
const DESKTOP_QUERY = "(min-width: 768px)";

/* ------------------------------------------------------------------ */
/* Rewards summary (off-typed-client route, same pattern as addons/ai) */
/* ------------------------------------------------------------------ */

type RewardsSummary = {
  points: number;
  pendingPoints: number;
  lifetimePoints: number;
  recent: { id: string; label: string; points: number; createdAt: string }[];
};

function useMyRewards(opts?: { enabled?: boolean }) {
  return useQuery<RewardsSummary>({
    queryKey: [...accountKeys.all, "rewards"],
    queryFn: async () =>
      unwrap(await (api as unknown as { me: { rewards: { get: () => Promise<unknown> } } }).me.rewards.get() as never),
    enabled: opts?.enabled,
  });
}

/* ------------------------------------------------------------------ */
/* Small hooks                                                         */
/* ------------------------------------------------------------------ */

export type TabId =
  | "overview"
  | "bookings"
  | "saved"
  | "reviews"
  | "details"
  | "security"
  | "rewards"
  | "preferences";

function isUpcoming(r: Reservation, now: number) {
  return (r.status === "pending" || r.status === "confirmed") && new Date(r.startAt).getTime() > now;
}

/** Pending or confirmed and still ahead, soonest first. */
function useUpcoming() {
  const reservations = useMyReservations();
  const upcoming = useMemo(() => {
    const now = Date.now();
    return (reservations.data ?? [])
      .filter((r) => isUpcoming(r, now))
      .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
  }, [reservations.data]);
  return { upcoming, loading: reservations.isLoading };
}

function useDateTimeFormat(locale: string) {
  return useMemo(
    () =>
      new Intl.DateTimeFormat(`${locale}-u-nu-latn`, {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [locale],
  );
}

/**
 * The popup only ever opens on a tap, so it never renders on the server and
 * can read the viewport on its first render.
 */
function useIsDesktop() {
  const [desktop, setDesktop] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(DESKTOP_QUERY).matches
      : true,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const on = () => setDesktop(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return desktop;
}

/** The page behind stays where it was while the popup is open. */
function useBodyScrollLock() {
  useEffect(() => {
    const body = document.body;
    const prev = body.style.overflow;
    body.style.overflow = "hidden";
    return () => {
      body.style.overflow = prev;
    };
  }, []);
}

/**
 * Online or away on the Casablanca clock, read only after mount and every
 * minute after. Null on the first render, so the neutral support hours show
 * until the client knows (the same rule as the booking page's help card).
 */
function useSupportOnline(): boolean | null {
  const [online, setOnline] = useState<boolean | null>(null);
  useEffect(() => {
    const tick = () => setOnline(isSupportOnline(new Date()));
    tick();
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, []);
  return online;
}

/**
 * One highlight behind a vertical list of buttons, moved with transform only.
 * Each option carries `data-tab`; the layer reads its offset and height.
 */
function useSlidingHighlight(active: string) {
  const list = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ y: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      const el = list.current?.querySelector<HTMLElement>(`[data-tab="${active}"]`);
      if (el) setBox({ y: el.offsetTop, h: el.offsetHeight });
    };
    measure();
    // Fonts arriving or a longer translation change the rows' heights.
    const ro = typeof ResizeObserver !== "undefined" && list.current ? new ResizeObserver(measure) : null;
    if (ro && list.current) ro.observe(list.current);
    return () => ro?.disconnect();
  }, [active]);
  return { list, box };
}

/* ------------------------------------------------------------------ */
/* Small primitives                                                    */
/* ------------------------------------------------------------------ */

function Eyebrow({ children, className = "mb-2" }: { children: ReactNode; className?: string }) {
  return (
    <p className={`text-[11px] font-bold uppercase ${className}`} style={{ color: MUTED, letterSpacing: "0.08em" }}>
      {children}
    </p>
  );
}

function Card({ children }: { children: ReactNode }) {
  return <div style={PANEL}>{children}</div>;
}

function RowDivider() {
  return <div style={{ height: 1, background: HAIRLINE }} className="mx-4" />;
}

function Avatar({ image, initials, size }: { image?: string; initials: string; size: number }) {
  return (
    <span
      className="grid shrink-0 place-items-center overflow-hidden rounded-full font-semibold text-white"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.34),
        background: NAVY_GRADIENT,
        boxShadow: `0 0 0 3px #fff, 0 0 0 4px ${CONTROL_EDGE}, 0 10px 22px -12px rgba(17,14,57,0.8)`,
      }}
    >
      {image ? <img src={image} alt="" className="h-full w-full object-cover" /> : initials || "•"}
    </span>
  );
}

function PointsChip({ points, locale }: { points: number | null; locale: string }) {
  const { t } = useTranslation();
  return (
    <span
      data-testid="points-chip"
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold tabular-nums"
      style={{ ...CONTROL, color: GOLD_DARK }}
    >
      <Sparkles size={12} color={GOLD_DARK} aria-hidden />
      {points === null ? "…" : formatNumber(points, locale)} {t("account_modal.points")}
    </span>
  );
}

function CountBadge({ n }: { n: number }) {
  return (
    <span
      data-testid="bookings-badge"
      className="ms-auto rounded-full px-1.5 text-[10.5px] font-bold tabular-nums"
      style={{ background: GOLD_TINT, color: GOLD_DARK }}
    >
      {n > 9 ? "9+" : n}
    </span>
  );
}

function CloseButton({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={t("common.close")}
      className="mj-press grid h-10 w-10 shrink-0 place-items-center rounded-full"
      style={{ ...CONTROL, color: NAVY }}
    >
      <X size={17} />
    </button>
  );
}

function GlassPill({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mj-press min-h-[36px] shrink-0 rounded-full px-4 text-[13px] font-semibold"
      style={{ ...CONTROL, color: NAVY }}
    >
      {children}
    </button>
  );
}

/**
 * The preferences chips, on the house dropdown.
 *
 * They were the only one of the site's four hand-written menus that already got
 * Escape and the capture phase right, which is exactly why that had to survive
 * the move: inside a modal, an Escape that travels on closes the whole sheet
 * when the traveller only meant to dismiss a list. `@majourneys/ui` keeps the
 * capture-phase listener and adds what this never had: arrow keys, Home and
 * End, Enter, and focus back on the chip.
 *
 * `group` is a `MenuGroup` rather than a name and a setter now. Same rule, same
 * effect: Currency and Language can never hang open at once.
 *
 * A chip at rest is white with one navy hairline, and the hairline goes navy
 * when the menu is open or a choice has been made. The band belongs to the
 * panel that opens.
 */
function ChipDropdown({
  name,
  group,
  title,
  options,
  value,
  onChange,
  labels,
}: {
  name: string;
  group: MenuGroup;
  title: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
  labels?: Record<string, string>;
}) {
  const { t } = useTranslation();
  return (
    <SelectMenu
      name={name}
      group={group}
      className="shrink-0"
      width={210}
      options={options.map((o) => ({ value: o, label: labels?.[o] ?? o }))}
      value={value}
      onChange={(next) => {
        if (next !== value) onChange(next);
      }}
      labels={{
        title,
        searchPlaceholder: t("common.menu_search", { defaultValue: "Search" }),
        clearSearch: t("common.menu_clear_search", { defaultValue: "Clear the search" }),
        closeList: t("common.menu_close_list", { defaultValue: "Close the list" }),
        emptyTitle: t("common.menu_empty", { defaultValue: "Nothing to choose from" }),
        noMatchTitle: () => t("common.menu_no_match", { defaultValue: "Nothing matches" }),
        noMatchHint: t("common.menu_no_match_hint", {
          defaultValue: "Try a shorter word, or clear the search.",
        }),
      }}
      // "Currency, MAD" rather than just "MAD": the row's heading beside it is
      // visual context only, so the button has to carry its own.
      triggerAriaLabel={`${title}, ${labels?.[value] ?? value}`}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Traveler details: tap-to-edit fields on real data                   */
/* ------------------------------------------------------------------ */

function EditableCard({
  icon: Icon,
  label,
  value,
  placeholder,
  onSave,
}: {
  icon: LucideIcon;
  label: string;
  value: string | null;
  placeholder: string;
  onSave: (v: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const [busy, setBusy] = useState(false);
  const empty = !value;

  const commit = async () => {
    const v = draft.trim();
    if (!v || v === value) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await onSave(v);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role={editing ? undefined : "button"}
      tabIndex={editing ? undefined : 0}
      onClick={
        editing
          ? undefined
          : () => {
              setDraft(value ?? "");
              setEditing(true);
            }
      }
      onKeyDown={
        editing
          ? undefined
          : (e) => {
              if (e.key === "Enter") {
                setDraft(value ?? "");
                setEditing(true);
              }
            }
      }
      className="cursor-pointer rounded-[16px] px-4 py-3.5 text-start"
      style={{ background: CONTROL_LIT, boxShadow: `0 0 0 1px ${editing ? CONTROL_EDGE_ON : CONTROL_EDGE}` }}
    >
      <div className="mb-1.5 flex items-center gap-2">
        <Icon size={14} color={empty ? GOLD_DARK : MUTED} aria-hidden />
        <span className="text-[12px] font-semibold" style={{ color: MUTED }}>
          {label}
        </span>
      </div>
      {editing ? (
        <form
          className="flex gap-2"
          onClick={(e) => e.stopPropagation()}
          onSubmit={(e) => {
            e.preventDefault();
            void commit();
          }}
        >
          <div
            className="min-w-0 flex-1 rounded-[11px]"
            style={{ height: 38, background: "#fff", boxShadow: `0 0 0 1px ${focused ? NAVY : CONTROL_EDGE}` }}
          >
            <input
              defaultValue={value ?? ""}
              placeholder={placeholder}
              autoFocus
              aria-label={label}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setEditing(false);
              }}
              onChange={(e) => setDraft(e.target.value)}
              className="h-full w-full rounded-[inherit] bg-transparent px-3 text-[13.5px] outline-none"
              style={{ color: NAVY }}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="mj-press shrink-0 rounded-full px-3.5 text-[12.5px] font-semibold text-white disabled:opacity-60"
            style={{ background: BEVEL_NAVY_FACE, boxShadow: BEVEL_INSET_NAVY }}
          >
            {busy ? "…" : t("account_modal.save")}
          </button>
        </form>
      ) : (
        <p className="[overflow-wrap:anywhere] text-[14px] font-semibold" style={{ color: value ? NAVY : "rgba(17,14,57,0.35)" }}>
          {value ?? placeholder}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Bookings: rows, status, the empty state                             */
/* ------------------------------------------------------------------ */

/** Confirmed (and how it is paid) or still waiting, as a dot and a word. */
function StatusDot({ r }: { r: Reservation }) {
  const { t } = useTranslation();
  // A desert camp is a stay, paid online from a secure link our team emails.
  const byLink = r.activities.some((line) => paysByLink(line.slug));
  const confirmed = r.status === "confirmed";
  const label = confirmed
    ? byLink
      ? t("account_modal.status_pill_confirmed_link")
      : t("account_modal.status_pill_confirmed")
    : t("account_modal.status_pill_pending");
  return (
    <span
      data-testid="status-dot"
      data-status={r.status}
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11.5px] font-semibold"
      style={{ ...CONTROL, color: NAVY }}
    >
      <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: confirmed ? ONLINE : GOLD }} />
      {label}
    </span>
  );
}

/** A thumbnail, or the section's icon when the row has no photo. */
function RowThumb({ src, icon: FallbackIcon, size = 46 }: { src?: string | null; icon: LucideIcon; size?: number }) {
  if (src) {
    return <img src={src} alt="" className="shrink-0 rounded-[12px] object-cover" style={{ width: size, height: size }} />;
  }
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-[12px]"
      style={{ width: size, height: size, background: GOLD_TINT }}
    >
      <FallbackIcon size={18} color={GOLD_DARK} aria-hidden />
    </span>
  );
}

/** An upcoming booking: framed, its status as a dot, WhatsApp one tap away. */
/**
 * When a booking happens, as the row prints it. A flexible package's window
 * reads "Mon 5 Oct, 13:00–15:00" (the team confirms the hour); every other
 * booking keeps the one formatted instant.
 */
function whenLabel(r: Reservation, fmt: Intl.DateTimeFormat): ReactNode {
  const d = new Date(r.startAt);
  if (!r.reservationTimeEnd) return fmt.format(d);
  const locale = fmt.resolvedOptions().locale;
  const day = new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short" }).format(d);
  const start = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(d);
  return (
    <>
      {day}, <span dir="ltr">{formatTimeRange(start, r.reservationTimeEnd)}</span>
    </>
  );
}

/**
 * Opens a booking's own page from a row (owner, 2026-10-26): the popup closes
 * first, then the page loads. A real link, so a middle click or a long press
 * still opens it in a new tab. Rows without a page stay plain.
 */
function BookingLink({
  r,
  locale,
  goHref,
  className,
  children,
}: {
  r: Reservation;
  locale: string;
  goHref: (href: string) => void;
  className: string;
  children: ReactNode;
}) {
  const href = bookingPageHref(r.ref, locale);
  if (!href) return <div className={className}>{children}</div>;
  return (
    <a
      href={href}
      className={`${className} mj-press rounded-[14px] outline-none focus-visible:ring-2 focus-visible:ring-[#E5B265]`}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        goHref(href);
      }}
    >
      {children}
    </a>
  );
}

function BookingRow({
  r,
  fmt,
  locale,
  goHref,
}: {
  r: Reservation;
  fmt: Intl.DateTimeFormat;
  locale: string;
  goHref: (href: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div data-testid="booking-row" className="p-2" style={PANEL}>
      <div className="flex items-center gap-3">
        <BookingLink r={r} locale={locale} goHref={goHref} className="flex min-w-0 flex-1 items-center gap-3">
          <RowThumb src={r.image} icon={Luggage} size={56} />
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 break-words text-[14px] font-semibold" style={{ color: NAVY }}>
              {r.title}
            </p>
            <p className="break-words text-[12.5px]" style={{ color: MUTED }}>
              {whenLabel(r, fmt)} · {t("account.guests_count", { count: r.guests })}
            </p>
            <div className="mt-1">
              <StatusDot r={r} />
            </div>
          </div>
        </BookingLink>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("account_modal.whatsapp_about_booking")}
          className="mj-press grid h-10 w-10 shrink-0 place-items-center rounded-full"
          style={{ ...CONTROL, color: NAVY }}
        >
          <MessageCircle size={16} aria-hidden />
        </a>
      </div>
    </div>
  );
}

const PICKS = [
  {
    key: "pick_activities",
    path: "category/activities/marrakech",
    img: "https://imagedelivery.net/9h9OrUsEhPYeYHCr6DaDTQ/f695e291-75bb-4734-71bd-64d915585600/w=360,fit=scale-down,format=auto",
  },
  {
    key: "pick_restaurants",
    path: "category/restaurants/marrakech",
    img: "https://imagedelivery.net/9h9OrUsEhPYeYHCr6DaDTQ/12b294a7-1958-498a-b02a-4a7a5b9f0f00/w=360,fit=scale-down,format=auto",
  },
  {
    key: "pick_spas",
    path: "category/spas/marrakech",
    img: "https://imagedelivery.net/9h9OrUsEhPYeYHCr6DaDTQ/35a7dc76-d5d7-4da2-c19e-837827b20200/w=360,fit=scale-down,format=auto",
  },
] as const;

/** Nothing booked: three places to start and MaJourneys AI, not a dead end. */
function FirstTripEmpty({ locale, goHref }: { locale: string; goHref: (href: string) => void }) {
  const { t } = useTranslation();
  return (
    <div data-testid="first-trip-empty" className="p-3.5" style={panelStyle(20)}>
      <p className="text-[14.5px] font-semibold" style={{ color: NAVY }}>
        {t("account_modal.no_upcoming_title")}
      </p>
      <p className="mt-0.5 text-[13px]" style={{ color: MUTED }}>
        {t("account_modal.no_upcoming_picks")}
      </p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {PICKS.map((p) => {
          const href = `/${locale}/${p.path}`;
          return (
            <a
              key={p.key}
              href={href}
              onClick={(e) => {
                e.preventDefault();
                goHref(href);
              }}
              className="mj-press block min-w-0 overflow-hidden rounded-[14px] text-start"
              style={CONTROL}
            >
              <img src={p.img} alt="" className="h-[70px] w-full object-cover" loading="lazy" />
              <span className="block px-2 py-1.5 text-[12px] font-semibold leading-tight" style={{ color: NAVY }}>
                {t(`account_modal.${p.key}`)}
              </span>
            </a>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => goHref(`/${locale}/ai-chat`)}
        className="mj-press mt-2.5 inline-flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-full px-3 text-[13px] font-semibold"
        style={{ ...CONTROL, color: NAVY }}
      >
        <Sparkles size={14} color={GOLD_DARK} aria-hidden /> {t("account_modal.ask_ai_plan")}
      </button>
    </div>
  );
}

/** Support, with whether someone is there right now. */
function SupportRow({ stacked = false }: { stacked?: boolean }) {
  const { t, i18n } = useTranslation();
  const online = useSupportOnline();
  return (
    <div data-testid="support-row" className="p-3" style={PANEL}>
      <div className={stacked ? "flex flex-wrap items-center gap-3" : "flex items-center gap-3"}>
        <span
          aria-hidden
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full"
          style={{ background: NAVY_GRADIENT }}
        >
          <MessageCircle size={17} color={GOLD} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold" style={{ color: NAVY }}>
            {t("account_modal.whatsapp_title")}
          </p>
          <p
            data-testid="support-status"
            data-state={online === null ? "unknown" : online ? "online" : "away"}
            className="flex items-center gap-1.5 text-[12.5px]"
            style={{ color: MUTED }}
          >
            {online === null ? (
              t("activity.support_hours", supportHoursVars(i18n.language))
            ) : (
              <>
                <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: online ? ONLINE : GOLD }} />
                {online ? t("activity.help_online") : t("activity.help_away", { time: formatSupportHour(SUPPORT_OPENS_HOUR, i18n.language) })}
              </>
            )}
          </p>
        </div>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={`mj-press inline-flex min-h-[40px] shrink-0 items-center justify-center gap-2 rounded-full px-3.5 text-[13px] font-semibold ${
            stacked ? "w-full" : ""
          }`}
          style={{ ...CONTROL, color: NAVY }}
        >
          <span aria-hidden className="h-2 w-2 rounded-full" style={{ background: WHATSAPP_GREEN }} />
          {t("account_modal.chat_with_us")}
        </a>
      </div>
    </div>
  );
}

function LoadingRows({ count = 3 }: { count?: number }) {
  return (
    <Card>
      {Array.from({ length: count }, (_, i) => (
        <div key={i}>
          {i > 0 ? <RowDivider /> : null}
          <div className="flex items-center gap-4 px-4 py-3.5">
            <span className="shrink-0 animate-pulse rounded-[12px]" style={{ width: 46, height: 46, background: SOFT_TINT }} />
            <span className="flex-1 space-y-2">
              <span className="block h-3 w-2/3 animate-pulse rounded-full" style={{ background: SOFT_TINT }} />
              <span className="block h-3 w-1/3 animate-pulse rounded-full" style={{ background: "rgba(17,14,57,0.04)" }} />
            </span>
          </div>
        </div>
      ))}
    </Card>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="px-5 py-6 text-center" style={panelStyle(18)}>
      <p className="text-[14px] font-semibold" style={{ color: NAVY }}>
        {title}
      </p>
      <p className="mt-1 text-[13px]" style={{ color: MUTED }}>
        {body}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Overview                                                            */
/* ------------------------------------------------------------------ */

/** Upcoming, Saved and Points: white tiles that open their own section. */
function StatTiles({ goTo, locale, compact }: { goTo: (t: TabId) => void; locale: string; compact: boolean }) {
  const { t } = useTranslation();
  const { upcoming, loading } = useUpcoming();
  // The shared store, so a heart tapped on a card counts here too. This tile
  // read /me/saved alone and showed 0 for anyone who only ever used the cards.
  const { slugs: savedSlugs } = useSaved();
  const rewards = useMyRewards();
  const points = rewards.data?.points ?? 0;

  const stats: { id: TabId; icon: LucideIcon; n: number | null; label: string; hint: string | null }[] = [
    {
      id: "bookings",
      icon: Luggage,
      n: loading ? null : upcoming.length,
      label: t("account_modal.stat_upcoming"),
      hint: !loading && upcoming.length === 0 ? t("account_modal.hint_upcoming_zero") : null,
    },
    {
      id: "saved",
      icon: Heart,
      n: savedSlugs.size,
      label: t("account_modal.stat_saved"),
      hint: savedSlugs.size === 0 ? t("account_modal.hint_saved_zero") : null,
    },
    {
      id: "rewards",
      icon: Medal,
      n: rewards.isLoading ? null : points,
      label: t("account_modal.stat_points"),
      hint: !rewards.isLoading && points === 0 ? t("account_modal.earn_rule") : null,
    },
  ];

  return (
    <div className={`grid grid-cols-3 ${compact ? "gap-2" : "gap-3"}`}>
      {stats.map((s) => (
        <button
          key={s.id}
          type="button"
          data-testid={`tile-${s.id}`}
          onClick={() => goTo(s.id)}
          className={`group mj-press min-w-0 text-start transition-transform md:hover:-translate-y-0.5 ${
            compact ? "rounded-[16px] p-2.5" : "rounded-[18px] p-3.5"
          }`}
          style={CONTROL}
        >
          <span
            aria-hidden
            className={`grid place-items-center rounded-full ${compact ? "h-7 w-7" : "h-8 w-8"}`}
            style={{ background: SOFT_TINT, color: GOLD_DARK }}
          >
            <s.icon size={compact ? 15 : 16} />
          </span>
          <span
            className={`block font-semibold leading-none tabular-nums ${compact ? "mt-2 text-[20px]" : "mt-2.5 text-[24px]"}`}
            style={{ color: NAVY }}
          >
            {s.n === null ? "…" : formatNumber(s.n, locale)}
          </span>
          <span className={`mt-1 block font-semibold ${compact ? "text-[12px]" : "text-[13px]"}`} style={{ color: NAVY }}>
            {s.label}
          </span>
          {!compact && s.hint ? (
            <span className="mt-0.5 flex items-start gap-1 text-[12px] leading-snug" style={{ color: MUTED }}>
              <span className="min-w-0">{s.hint}</span>
              <ChevronRight
                size={12}
                aria-hidden
                className="mt-[3px] shrink-0 opacity-0 transition-opacity group-hover:opacity-100 rtl:-scale-x-100"
              />
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

function NextUp({
  locale,
  goHref,
  limit,
}: {
  locale: string;
  goHref: (href: string) => void;
  limit: number;
}) {
  const { t } = useTranslation();
  const { upcoming, loading } = useUpcoming();
  const fmt = useDateTimeFormat(locale);
  return (
    <div>
      <Eyebrow>{t("account_modal.next_up")}</Eyebrow>
      {loading ? (
        <LoadingRows count={1} />
      ) : upcoming.length === 0 ? (
        <FirstTripEmpty locale={locale} goHref={goHref} />
      ) : (
        <div className="flex flex-col gap-2">
          {upcoming.slice(0, limit).map((r) => (
            <BookingRow key={r.id} r={r} fmt={fmt} locale={locale} goHref={goHref} />
          ))}
        </div>
      )}
    </div>
  );
}

function OverviewTab({
  goTo,
  goHref,
  locale,
}: {
  goTo: (t: TabId) => void;
  goHref: (href: string) => void;
  locale: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-5">
      <StatTiles goTo={goTo} locale={locale} compact={false} />
      <NextUp locale={locale} goHref={goHref} limit={2} />
      <div>
        <Eyebrow>{t("account_modal.support")}</Eyebrow>
        <SupportRow />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Reservations, Saved, Reviews: the three the old /account page held  */
/* ------------------------------------------------------------------ */

const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  pending: { bg: "rgba(229,178,101,0.20)", fg: GOLD_DARK },
  confirmed: { bg: "rgba(31,168,85,0.15)", fg: "#1A7A44" },
  completed: { bg: "rgba(17,14,57,0.08)", fg: NAVY },
  cancelled: { bg: "rgba(190,60,60,0.13)", fg: "#9B2C2C" },
  approved: { bg: "rgba(31,168,85,0.15)", fg: "#1A7A44" },
  rejected: { bg: "rgba(190,60,60,0.13)", fg: "#9B2C2C" },
};

function StatusChip({ status, label }: { status: string; label: string }) {
  const tone = STATUS_TONE[status] ?? STATUS_TONE.completed!;
  return (
    <span
      className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase"
      style={{ letterSpacing: "0.04em", background: tone.bg, color: tone.fg }}
    >
      {label}
    </span>
  );
}

/**
 * Every reservation, newest first: the list that used to mean leaving the
 * modal for /account?tab=bookings. Upcoming ones are separated out because
 * that is the only group anyone opens this for in a hurry.
 */
function BookingsTab({ locale, goHref }: { locale: string; goHref: (href: string) => void }) {
  const { t } = useTranslation();
  const reservations = useMyReservations();
  const fmt = useDateTimeFormat(locale);

  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const all = [...(reservations.data ?? [])].sort(
      (a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime(),
    );
    return {
      upcoming: all
        .filter((r) => isUpcoming(r, now))
        .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()),
      past: all.filter((r) => !isUpcoming(r, now)),
    };
  }, [reservations.data]);

  if (reservations.isLoading) return <LoadingRows />;

  if ((reservations.data ?? []).length === 0) {
    return <FirstTripEmpty locale={locale} goHref={goHref} />;
  }

  return (
    <div className="flex flex-col gap-5">
      {upcoming.length > 0 ? (
        <div>
          <Eyebrow>{t("account_modal.upcoming")}</Eyebrow>
          <div className="flex flex-col gap-2">
            {upcoming.map((r) => (
              <BookingRow key={r.id} r={r} fmt={fmt} locale={locale} goHref={goHref} />
            ))}
          </div>
        </div>
      ) : null}

      {past.length > 0 ? (
        <div>
          <Eyebrow>{t("account_modal.past")}</Eyebrow>
          <Card>
            {past.map((r, i) => (
              <div key={r.id}>
                {i > 0 ? <RowDivider /> : null}
                <BookingLink r={r} locale={locale} goHref={goHref} className="flex items-center gap-4 px-4 py-3.5">
                  <RowThumb src={r.image} icon={Luggage} />
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 break-words text-[14px] font-semibold" style={{ color: NAVY }}>
                      {r.title}
                    </p>
                    <p className="break-words text-[13px]" style={{ color: MUTED }}>
                      {whenLabel(r, fmt)} · {t("account.guests_count", { count: r.guests })}
                    </p>
                  </div>
                  <StatusChip
                    status={r.status}
                    label={t(`account_modal.status_chip_${r.status}`, { defaultValue: r.status })}
                  />
                </BookingLink>
              </div>
            ))}
          </Card>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => goHref("/account?tab=bookings")}
        className="mj-press min-h-[44px] w-full rounded-full text-[13px] font-semibold"
        style={{ ...CONTROL, color: NAVY }}
      >
        {t("account_modal.open_full_bookings")}
      </button>
    </div>
  );
}

/** Saved items, each a link straight back to the thing that was saved. */
function SavedTab({ goHref }: { goHref: (href: string) => void }) {
  const { t } = useTranslation();
  const saved = useMySaved();
  const displayPrice = useDisplayPrice();

  if (saved.isLoading) return <LoadingRows />;

  const items = saved.data ?? [];
  if (items.length === 0) {
    return <EmptyState title={t("account_modal.no_saved_title")} body={t("account_modal.no_saved_body")} />;
  }

  return (
    <Card>
      {items.map((s, i) => (
        <div key={s.id}>
          {i > 0 ? <RowDivider /> : null}
          <button
            type="button"
            onClick={() => goHref(`/${s.slug}`)}
            className="flex w-full items-center gap-4 px-4 py-3.5 text-start"
          >
            <RowThumb src={s.image} icon={Heart} />
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 break-words text-[14px] font-semibold" style={{ color: NAVY }}>
                {s.title}
              </p>
              <p className="text-[13px]" style={{ color: MUTED }}>
                {s.city}
              </p>
            </div>
            {/* No "MAD 0": a restaurant table or an editorial place has no
                price, so the line is left out (owner rule, 2026-10-30). */}
            {showsActivityPrice({
              price: Number(s.price),
              mode: s.mode,
              categorySlug: s.categorySlug,
              segment: s.segment,
            }) ? (
              <span className="shrink-0 text-[13px] font-semibold" style={{ color: NAVY }}>
                {displayPrice(Number(s.price))}
              </span>
            ) : null}
          </button>
        </div>
      ))}
    </Card>
  );
}

/** Reviews the traveller has written, with the moderation state showing. */
function ReviewsTab({ locale }: { locale: string }) {
  const { t } = useTranslation();
  const reviews = useMyReviews();

  const fmt = useMemo(
    () => new Intl.DateTimeFormat(`${locale}-u-nu-latn`, { day: "numeric", month: "short", year: "numeric" }),
    [locale],
  );

  if (reviews.isLoading) return <LoadingRows />;

  const items = reviews.data ?? [];
  if (items.length === 0) {
    return <EmptyState title={t("account_modal.no_reviews_title")} body={t("account_modal.no_reviews_body")} />;
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((r) => (
        <Card key={r.id}>
          <div className="px-4 py-3.5">
            <div className="flex items-start gap-4">
              <RowThumb src={r.activityImage} icon={Sparkles} />
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 break-words text-[14px] font-semibold" style={{ color: NAVY }}>
                  {r.activityTitle ?? t("account_modal.review_activity_gone")}
                </p>
                <p className="flex items-center gap-1.5 text-[13px]" style={{ color: MUTED }}>
                  <span style={{ color: GOLD_DARK }}>{"★".repeat(Math.max(1, Math.round(r.rating)))}</span>
                  {fmt.format(new Date(r.createdAt))}
                </p>
              </div>
              <StatusChip
                status={r.status}
                label={t(`account_modal.review_status_${r.status}`, { defaultValue: r.status })}
              />
            </div>
            {r.body ? (
              <p className="mt-2.5 text-[13px] leading-relaxed" style={{ color: MUTED }}>
                {r.body}
              </p>
            ) : null}
            {/* The reason is the whole value of a rejection: without it the
                chip just says no. */}
            {r.status === "rejected" && r.rejectionReason ? (
              <p className="mt-2 text-[12.5px]" style={{ color: "#9B2C2C" }}>
                {r.rejectionReason}
              </p>
            ) : null}
          </div>
        </Card>
      ))}
    </div>
  );
}

function DetailsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const profile = useMyProfile();

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: accountKeys.profile() });
  };

  const patchProfile = async (fields: { firstName?: string; lastName?: string }) => {
    await (
      api as unknown as {
        me: { profile: { patch: (b: object) => Promise<unknown> } };
      }
    ).me.profile.patch(fields);
    refresh();
  };

  const saveLocation = async (location: string) => {
    const prefs = profile.data?.preferences;
    if (!prefs) return;
    await profileApi.updatePreferences({ ...prefs, location });
    refresh();
  };

  const email = profile.data?.user.email ?? (session?.user?.email as string | undefined) ?? null;
  const verified = profile.data?.user.emailVerified ?? false;

  return (
    <div>
      <Eyebrow>{t("account_modal.personal_info")}</Eyebrow>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <EditableCard
          icon={User}
          label={t("account_modal.field_first_name")}
          value={profile.data?.user.firstName || null}
          placeholder={t("account_modal.field_first_name_ph")}
          onSave={(v) => patchProfile({ firstName: v })}
        />
        <EditableCard
          icon={User}
          label={t("account_modal.field_last_name")}
          value={profile.data?.user.lastName || null}
          placeholder={t("account_modal.field_last_name_ph")}
          onSave={(v) => patchProfile({ lastName: v })}
        />
        <div className="rounded-[16px] px-4 py-3.5" style={CONTROL}>
          <div className="mb-1.5 flex items-center gap-2">
            <Mail size={14} color={MUTED} aria-hidden />
            <span className="text-[12px] font-semibold" style={{ color: MUTED }}>
              {t("account_modal.field_email")}
            </span>
            {verified ? (
              <span
                className="ms-auto flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
                style={{ background: "rgba(31,168,85,0.15)", color: "#1A7A44" }}
              >
                <Check size={10} aria-hidden />
                {t("account_modal.verified")}
              </span>
            ) : null}
          </div>
          {/* clamp-ok: guest's own email */}
          <p className="truncate text-[14px] font-semibold" style={{ color: NAVY }}>
            {email ?? "…"}
          </p>
        </div>
        <EditableCard
          icon={MapPin}
          label={t("account_modal.field_location")}
          value={profile.data?.preferences.location || null}
          placeholder={t("account_modal.field_location_ph")}
          onSave={saveLocation}
        />
      </div>
    </div>
  );
}

function RewardsTab({ locale }: { locale: string }) {
  const { t } = useTranslation();
  const rewards = useMyRewards();
  const points = rewards.data?.points ?? 0;
  const recent = rewards.data?.recent ?? [];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-6 px-5 py-5" style={panelStyle(20)}>
        <div className="min-w-0 flex-1">
          <p className="text-[13px]" style={{ color: MUTED }}>
            {t("account_modal.balance")}
          </p>
          <p className="text-[34px] font-semibold leading-tight tabular-nums" style={{ color: NAVY }}>
            {rewards.isLoading ? "…" : formatNumber(points, locale)}{" "}
            <span className="text-[15px] font-semibold" style={{ color: GOLD_DARK }}>
              {t("account_modal.points")}
            </span>
          </p>
          <p className="mt-0.5 text-[13px]" style={{ color: MUTED }}>
            {t("account_modal.earn_rule")}
          </p>
        </div>
        <span
          aria-hidden
          className="flex shrink-0 items-center justify-center rounded-full"
          style={{ width: 64, height: 64, background: NAVY_GRADIENT }}
        >
          <Medal size={28} color={GOLD} />
        </span>
      </div>

      <div>
        <Eyebrow>{t("account_modal.recent_activity")}</Eyebrow>
        {recent.length === 0 ? (
          <div className="px-5 py-6 text-center" style={panelStyle(18)}>
            <p className="text-[13px]" style={{ color: MUTED }}>
              {t("account_modal.no_points_yet")}
            </p>
          </div>
        ) : (
          <Card>
            {recent.slice(0, 5).map((r, idx) => (
              <div key={r.id}>
                {idx > 0 ? <RowDivider /> : null}
                <div className="flex items-center gap-4 px-4 py-3.5">
                  <p className="min-w-0 flex-1 line-clamp-2 text-[14px]" style={{ color: NAVY }}>
                    {r.label}
                  </p>
                  <span
                    className="text-[14px] font-semibold tabular-nums"
                    style={{ color: r.points >= 0 ? GOLD_DARK : "#C2404A" }}
                  >
                    {r.points >= 0 ? `+${formatNumber(r.points, locale)}` : formatNumber(r.points, locale)}
                  </span>
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}

const LANG_LABELS: Record<string, string> = {
  en: "English",
  fr: "Français",
  ar: "العربية",
  es: "Español",
  it: "Italiano",
  de: "Deutsch",
  nl: "Nederlands",
};

function PreferencesTab({ locale, onClose }: { locale: string; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const href = useRouterState({ select: (s) => s.location.href });
  // The prices on the page read the currency context, not the account row.
  // Showing the row here is what let this tab claim EUR while everything
  // outside it stayed in MAD.
  const { currency, setCurrency: setDisplayCurrency } = useCurrency();
  const { save } = useSavePreferences();
  // One menu at a time on this panel: opening Language closes Currency, and
  // the swap skips the grow-in.
  const menuGroup = useMenuGroup();

  const pickCurrency = async (next: string) => {
    setDisplayCurrency(next as CurrencyCode);
    await save({ currency: next });
  };

  const setLanguage = (next: string) => {
    // Saving before we navigate: switchLocale unmounts this tab.
    void save({ language: next });
    onClose();
    navigate({ href: switchLocale(href, next as never) });
  };

  return (
    <div className="relative z-10">
      <Eyebrow>{t("account_modal.lang_currency")}</Eyebrow>
      <Card>
        <div className="flex items-center gap-4 px-4 py-3.5">
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold" style={{ color: NAVY }}>
              {t("account_modal.currency")}
            </p>
            <p className="text-[13px]" style={{ color: MUTED }}>
              {t("account_modal.currency_body")}
            </p>
          </div>
          <ChipDropdown
            name="currency"
            group={menuGroup}
            title={t("account_modal.currency")}
            options={["MAD", "EUR", "USD"]}
            value={currency}
            onChange={(v) => void pickCurrency(v)}
          />
        </div>
        <RowDivider />
        <div className="flex items-center gap-4 px-4 py-3.5">
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold" style={{ color: NAVY }}>
              {t("account_modal.language")}
            </p>
            <p className="text-[13px]" style={{ color: MUTED }}>
              {t("account_modal.language_body")}
            </p>
          </div>
          <ChipDropdown
            name="language"
            group={menuGroup}
            title={t("account_modal.language")}
            options={Object.keys(LANG_LABELS)}
            value={locale in LANG_LABELS ? locale : "en"}
            labels={LANG_LABELS}
            onChange={setLanguage}
          />
        </div>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The modal                                                           */
/* ------------------------------------------------------------------ */

const TAB_ICONS: Record<TabId, LucideIcon> = {
  overview: User,
  bookings: Luggage,
  saved: Heart,
  reviews: Star,
  details: IdCard,
  security: ShieldCheck,
  rewards: Medal,
  preferences: SettingsIcon,
};

const TAB_ORDER: TabId[] = ["overview", "bookings", "saved", "reviews", "details", "security", "rewards", "preferences"];

/** How long the phone sheet takes to leave before it unmounts. */
const SHEET_EXIT_MS = 260;

export function AccountModal({
  onClose,
  initialTab = "overview",
}: {
  onClose: () => void;
  /** Which panel the modal lands on. The header menu jumps straight to
      Reservations or Saved rather than routing away to /account. On a phone
      anything but the overview opens straight at that section. */
  initialTab?: TabId;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const profile = useMyProfile();
  const rewards = useMyRewards();
  const { upcoming } = useUpcoming();
  const desktop = useIsDesktop();
  useBodyScrollLock();

  /** The section on screen: the desktop's chosen tab, the phone's level 2. */
  const [tab, setTab] = useState<TabId>(initialTab);
  /** Phone only: whether level 2 is in. `tab` stays put while it slides out. */
  const [levelTwo, setLevelTwo] = useState(initialTab !== "overview");
  const [closing, setClosing] = useState(false);

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locale = /^\/(en|fr|ar|es|it|de|nl)(\/|$)/.exec(pathname)?.[1] ?? "en";

  const user = session?.user as { name?: string; email?: string; image?: string } | undefined;
  const displayName = profile.data?.user.name || user?.name || user?.email || "";
  const email = profile.data?.user.email ?? user?.email ?? null;
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
  const points = rewards.data ? rewards.data.points : null;

  // The phone sheet leaves the way it came before it unmounts; the desktop
  // popup closes at once, as it always has.
  const exitTimer = useRef<number | null>(null);
  useEffect(() => () => {
    if (exitTimer.current) window.clearTimeout(exitTimer.current);
  }, []);
  const requestClose = () => {
    if (desktop) {
      onClose();
      return;
    }
    if (closing) return;
    setClosing(true);
    exitTimer.current = window.setTimeout(onClose, SHEET_EXIT_MS);
  };

  // Leaving the popup for another page: close it, then go. Closing never
  // touches the page's own URL, so its query string survives.
  const goHref = (href: string) => {
    onClose();
    navigate({ href });
  };

  const openSection = (id: TabId) => {
    setTab(id);
    if (!desktop) setLevelTwo(id !== "overview");
  };

  const handleSignOut = async () => {
    onClose();
    await signOut();
    navigate({ href: "/sign-in" });
  };

  const handleDeleted = () => {
    onClose();
    navigate({ href: `/${locale}` });
  };

  const label = (id: TabId) =>
    id === "overview"
      ? t("account_modal.tab_overview")
      : id === "security"
        ? t("account_modal.tab_security")
        : t(`account_modal.tab_${id}`);
  const title = (id: TabId) => (id === "overview" ? t("account_modal.title_overview") : label(id));

  const section = (id: TabId) => {
    switch (id) {
      case "overview":
        return <OverviewTab goTo={openSection} goHref={goHref} locale={locale} />;
      case "bookings":
        return <BookingsTab locale={locale} goHref={goHref} />;
      case "saved":
        return <SavedTab goHref={goHref} />;
      case "reviews":
        return <ReviewsTab locale={locale} />;
      case "details":
        return <DetailsTab />;
      case "security":
        return <SecurityPanel email={email} onDeleted={handleDeleted} />;
      case "rewards":
        return <RewardsTab locale={locale} />;
      case "preferences":
        return <PreferencesTab locale={locale} onClose={onClose} />;
    }
  };

  const quietRow =
    "flex min-h-[40px] w-full items-center gap-3 rounded-[12px] px-3 text-start text-[13.5px] transition-colors hover:bg-black/[0.04]";

  const helpAndSignOut = (
    <>
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={quietRow} style={{ color: MUTED }}>
        <HelpCircle size={16} aria-hidden />
        {t("account_modal.get_help")}
      </a>
      <button
        type="button"
        data-testid="sign-out"
        onClick={() => void handleSignOut()}
        className={quietRow}
        style={{ color: MUTED }}
      >
        <LogOut size={16} aria-hidden />
        {t("common.sign_out")}
      </button>
    </>
  );

  // Portal to <body>: the site header has backdrop-filter, which makes any
  // ancestor with a filter the containing block for position:fixed. Rendered
  // in place, the modal lays out inside the header instead of the viewport.
  return createPortal(
    desktop ? (
      <DesktopPopup
        tab={tab}
        setTab={setTab}
        onClose={requestClose}
        label={label}
        title={title}
        section={section}
        header={{ displayName, email, initials, image: user?.image, points, locale }}
        upcomingCount={upcoming.length}
        footer={helpAndSignOut}
      />
    ) : (
      <PhoneSheet
        tab={tab}
        levelTwo={levelTwo}
        closing={closing}
        openSection={openSection}
        back={() => setLevelTwo(false)}
        onClose={requestClose}
        label={label}
        title={title}
        section={section}
        header={{ displayName, email, initials, image: user?.image, points, locale }}
        upcomingCount={upcoming.length}
        goHref={goHref}
        footer={helpAndSignOut}
      />
    ),
    document.body,
  );
}

type HeaderInfo = {
  displayName: string;
  email: string | null;
  initials: string;
  image?: string;
  points: number | null;
  locale: string;
};

/* ------------------------------------------------------------------ */
/* Computer: the popup with a sidebar                                  */
/* ------------------------------------------------------------------ */

function DesktopPopup({
  tab,
  setTab,
  onClose,
  label,
  title,
  section,
  header,
  upcomingCount,
  footer,
}: {
  tab: TabId;
  setTab: (t: TabId) => void;
  onClose: () => void;
  label: (id: TabId) => string;
  title: (id: TabId) => string;
  section: (id: TabId) => ReactNode;
  header: HeaderInfo;
  upcomingCount: number;
  footer: ReactNode;
}) {
  const { t } = useTranslation();
  const { list, box } = useSlidingHighlight(tab);
  // The fill must be an image layer (solid()): a bare colour is invalid in the
  // layered background and the whole popup turned see-through.
  const frame = panelStyle(30, solid("#fff"));

  return (
    <div
      className="mj-fade-in fixed inset-0 z-[var(--z-modal)] flex items-center justify-center p-4 font-sans sm:p-6"
      style={{ background: "rgba(11,9,40,0.4)" }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("account_modal.title_overview")}
        data-testid="account-popup"
        className="mj-rise-in flex w-full max-w-[940px] overflow-hidden"
        style={{
          ...frame,
          boxShadow: `${frame.boxShadow}, ${FLOAT_SHADOW}`,
          // dvh, not vh: iOS Safari's 100vh includes the hidden toolbar, which
          // pushed the panel's top edge off-screen.
          height: "min(660px, calc(100dvh / var(--mj-ui-zoom, 1) - 32px))",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <aside
          className="flex w-[256px] shrink-0 flex-col border-e p-5"
          style={{ borderColor: HAIRLINE, background: "linear-gradient(180deg, #F7F7FB 0%, #F1F2F8 100%)" }}
        >
          <Avatar image={header.image} initials={header.initials} size={56} />
          <p className="mt-3 line-clamp-2 break-words text-[16px] font-semibold leading-tight" style={{ color: NAVY }}>
            {header.displayName}
          </p>
          {/* clamp-ok: guest's own email */}
          <p className="mt-0.5 truncate text-[12.5px]" style={{ color: MUTED }}>
            {header.email}
          </p>
          <div className="mt-2.5">
            <PointsChip points={header.points} locale={header.locale} />
          </div>

          <nav aria-label={t("account_modal.title_overview")} className="mt-5">
            <div ref={list} className="relative flex flex-col gap-0.5">
              {box ? (
                <span
                  aria-hidden
                  data-testid="nav-highlight"
                  data-active={tab}
                  className="mj-hl pointer-events-none absolute inset-x-0 top-0 rounded-[12px]"
                  style={{
                    height: box.h,
                    transform: `translateY(${box.y}px)`,
                    background: CONTROL_LIT,
                    boxShadow: `0 0 0 1px ${CONTROL_EDGE}, 0 6px 14px -10px rgba(17,14,57,0.5)`,
                  }}
                />
              ) : null}
              {TAB_ORDER.map((id) => {
                const active = id === tab;
                const Icon = TAB_ICONS[id];
                return (
                  <button
                    key={id}
                    type="button"
                    data-tab={id}
                    aria-current={active ? "page" : undefined}
                    onClick={() => setTab(id)}
                    className={`mj-hl-text relative z-[1] flex min-h-[40px] items-center gap-3 rounded-[12px] px-3 text-start text-[13.5px] ${
                      active ? "font-semibold" : ""
                    }`}
                    style={{ color: active ? NAVY : MUTED }}
                  >
                    <Icon size={16} aria-hidden style={{ color: active ? GOLD_DARK : MUTED }} />
                    <span className="min-w-0 break-words">{label(id)}</span>
                    {id === "bookings" && upcomingCount > 0 ? <CountBadge n={upcomingCount} /> : null}
                  </button>
                );
              })}
            </div>
          </nav>

          <div className="mt-auto flex flex-col gap-0.5 border-t pt-3" style={{ borderColor: HAIRLINE }}>
            {footer}
          </div>
        </aside>

        <div className="relative flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-3 px-7 pb-4 pt-6">
            <h2 className="font-heading text-[22px] font-bold" style={{ color: NAVY, letterSpacing: "-0.02em" }}>
              {title(tab)}
            </h2>
            <CloseButton onClose={onClose} />
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain px-7 pb-7 pt-1">
            <div key={tab} className="mj-rise-in">
              {section(tab)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Phone: a sheet with two levels, like iPhone Settings                */
/* ------------------------------------------------------------------ */

/** How far down the sheet has to be pulled before letting go closes it. */
const SHEET_DRAG_CLOSE_PX = 100;
/** A quick flick closes it from a shorter pull (px per ms). */
const SHEET_FLICK_SPEED = 0.6;
const SHEET_GONE = "translateY(calc(100% + 10px + env(safe-area-inset-bottom, 0px)))";

/**
 * Pull the phone sheet down to close it, from the handle or from a list that
 * is already scrolled to its top, the way an iPhone sheet behaves. A gesture
 * that starts as a scroll stays a scroll. Touch events, not pointer events:
 * iOS Safari cancels the pointer the moment it decides the finger scrolls.
 *
 * Once dragged, the sheet's open animation is switched off inline (a CSS
 * animation outranks an inline transform), so from then on its exit is a
 * transition this hook runs too.
 */
function useSheetDragToClose(
  sheetRef: RefObject<HTMLDivElement | null>,
  onClose: () => void,
  closing: boolean,
) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    let g: {
      x: number;
      y: number;
      t: number;
      scroller: HTMLElement | null;
      mode: "undecided" | "drag" | "scroll";
      dy: number;
    } | null = null;

    const move = (dy: number, animate: boolean) => {
      sheet.style.animation = "none";
      sheet.style.transition = animate ? "transform 260ms var(--mj-ease-drawer)" : "none";
      sheet.style.transform = dy > 0 ? `translateY(${dy}px)` : "none";
    };

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        g = null;
        return;
      }
      const target = e.target as HTMLElement;
      const touch = e.touches[0]!;
      g = {
        x: touch.clientX,
        y: touch.clientY,
        t: e.timeStamp,
        scroller: target.closest<HTMLElement>("[data-sheet-scroller]"),
        mode: "undecided",
        dy: 0,
      };
    };

    const onMove = (e: TouchEvent) => {
      if (!g || g.mode === "scroll") return;
      const touch = e.touches[0]!;
      const dx = touch.clientX - g.x;
      const dy = touch.clientY - g.y;
      if (g.mode === "undecided") {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        const atTop = !g.scroller || g.scroller.scrollTop <= 0;
        // Down, mostly vertical, with nothing above to scroll back to: a drag.
        g.mode = dy > 0 && Math.abs(dy) > Math.abs(dx) && atTop ? "drag" : "scroll";
        if (g.mode === "scroll") return;
        // Measure from here so the sheet doesn't jump the 4px slop.
        g.y = touch.clientY;
        g.t = e.timeStamp;
      }
      e.preventDefault();
      g.dy = Math.max(0, touch.clientY - g.y);
      move(g.dy, false);
    };

    const onEnd = (e: TouchEvent) => {
      if (!g || g.mode !== "drag") {
        g = null;
        return;
      }
      const speed = g.dy / Math.max(1, e.timeStamp - g.t);
      const close = g.dy > SHEET_DRAG_CLOSE_PX || (g.dy > 30 && speed > SHEET_FLICK_SPEED);
      g = null;
      if (close) closeRef.current();
      else move(0, true);
    };

    sheet.addEventListener("touchstart", onStart, { passive: true });
    // Not passive: a drag has to stop the page (and iOS's bounce) moving with it.
    sheet.addEventListener("touchmove", onMove, { passive: false });
    sheet.addEventListener("touchend", onEnd);
    sheet.addEventListener("touchcancel", onEnd);
    return () => {
      sheet.removeEventListener("touchstart", onStart);
      sheet.removeEventListener("touchmove", onMove);
      sheet.removeEventListener("touchend", onEnd);
      sheet.removeEventListener("touchcancel", onEnd);
    };
  }, [sheetRef]);

  // Closing after a drag: the stylesheet's exit animation is off, so slide
  // out from wherever the finger left the sheet.
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!closing || !sheet || sheet.style.animation !== "none") return;
    sheet.style.transition = "transform 260ms var(--mj-ease-drawer)";
    sheet.style.transform = SHEET_GONE;
  }, [closing, sheetRef]);
}

function PhoneSheet({
  tab,
  levelTwo,
  closing,
  openSection,
  back,
  onClose,
  label,
  title,
  section,
  header,
  upcomingCount,
  goHref,
  footer,
}: {
  tab: TabId;
  levelTwo: boolean;
  closing: boolean;
  openSection: (id: TabId) => void;
  back: () => void;
  onClose: () => void;
  label: (id: TabId) => string;
  title: (id: TabId) => string;
  section: (id: TabId) => ReactNode;
  header: HeaderInfo;
  upcomingCount: number;
  goHref: (href: string) => void;
  footer: ReactNode;
}) {
  const { t } = useTranslation();
  const showTwo = levelTwo && tab !== "overview";
  const sheetRef = useRef<HTMLDivElement>(null);
  useSheetDragToClose(sheetRef, onClose, closing);

  return (
    <div
      className="mj-acc-phone fixed inset-0 z-[var(--z-modal)] font-sans"
      data-state={closing ? "closed" : "open"}
      data-testid="account-sheet"
    >
      <div aria-hidden className="mj-acc-shade absolute inset-0" style={{ background: "rgba(11,9,40,0.42)" }} onClick={onClose} />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("account_modal.title_overview")}
        className="mj-acc-sheet absolute min-w-0 overflow-hidden"
        style={{
          // The pewter band, floating 10px in from the sides and lifted off
          // the bottom above the safe area (owner, 2026-10-11).
          ...SHEET_FRAME,
          insetInline: SHEET_INSET,
          bottom: SHEET_BOTTOM,
          top: "calc(env(safe-area-inset-top, 0px) + 32px)",
        }}
      >
        <div aria-hidden className="mx-auto mt-2 h-1.5 w-10 rounded-full" style={{ background: "rgba(17,14,57,0.18)" }} />

        {/* Level 1: who you are, the tiles, next up, and every section as a row. */}
        <div
          data-testid="sheet-level-1"
          data-sheet-scroller
          className="mj-acc-l1 absolute inset-x-0 bottom-0 top-4 overflow-y-auto overscroll-contain px-4 pt-3"
          data-pushed={showTwo ? "true" : "false"}
          aria-hidden={showTwo || undefined}
          inert={showTwo || undefined}
          style={{ paddingBottom: 24 }}
        >
          <div className="flex items-center gap-3">
            <Avatar image={header.image} initials={header.initials} size={48} />
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 break-words font-heading text-[16px] font-bold" style={{ color: NAVY }}>
                {header.displayName}
              </p>
              {/* clamp-ok: guest's own email */}
              <p className="truncate text-[12.5px]" style={{ color: MUTED }}>
                {header.email}
              </p>
            </div>
            <CloseButton onClose={onClose} />
          </div>
          <div className="mt-2.5">
            <PointsChip points={header.points} locale={header.locale} />
          </div>

          <div className="mt-4">
            <StatTiles goTo={openSection} locale={header.locale} compact />
          </div>

          <div className="mt-5">
            <NextUp locale={header.locale} goHref={goHref} limit={1} />
          </div>

          <Eyebrow className="mb-2 mt-5">{t("account_modal.title_overview")}</Eyebrow>
          <div className="p-1" style={PANEL}>
            {TAB_ORDER.filter((id) => id !== "overview").map((id, i) => {
              const Icon = TAB_ICONS[id];
              const badge = id === "bookings" && upcomingCount > 0;
              return (
                <button
                  key={id}
                  type="button"
                  data-testid={`row-${id}`}
                  onClick={() => openSection(id)}
                  className={`flex min-h-[48px] w-full items-center gap-3 px-3 text-start text-[14px] font-semibold ${
                    i ? "border-t" : ""
                  }`}
                  style={{ color: NAVY, borderColor: HAIRLINE }}
                >
                  <Icon size={16} aria-hidden style={{ color: GOLD_DARK }} />
                  <span className="min-w-0 break-words">{label(id)}</span>
                  {badge ? <CountBadge n={upcomingCount} /> : null}
                  <ChevronRight
                    size={16}
                    aria-hidden
                    className={`${badge ? "" : "ms-auto"} shrink-0 rtl:-scale-x-100`}
                    style={{ color: MUTED }}
                  />
                </button>
              );
            })}
          </div>

          <div className="mt-4">
            <SupportRow stacked />
          </div>

          <div className="mt-3 flex flex-col">{footer}</div>
        </div>

        {/* Level 2: one section, slid in from the end. */}
        <div
          data-testid="sheet-level-2"
          data-sheet-scroller
          className="mj-acc-l2 absolute inset-x-0 bottom-0 top-4 overflow-y-auto overscroll-contain px-4 pt-3"
          data-open={showTwo ? "true" : "false"}
          aria-hidden={!showTwo || undefined}
          inert={!showTwo || undefined}
          style={{ background: SHEET_FILL, paddingBottom: 24 }}
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={back}
              aria-label={t("common.back")}
              className="mj-press grid h-10 w-10 shrink-0 place-items-center rounded-full"
              style={{ ...CONTROL, color: NAVY }}
            >
              <ChevronLeft size={18} className="rtl:-scale-x-100" aria-hidden />
            </button>
            <h2 className="min-w-0 flex-1 break-words [text-wrap:balance] font-heading text-[18px] font-bold" style={{ color: NAVY }}>
              {title(tab)}
            </h2>
            <CloseButton onClose={onClose} />
          </div>
          <div key={tab} className="mt-4">
            {tab !== "overview" ? section(tab) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
